import { QUALIFIES_FOR_BONUS_FIELD } from '../../../../constants';

const PERCENTAGE_FIELDS = ['percent'];
const isPercentageField = (field: string) =>
  PERCENTAGE_FIELDS.some((f) => field.toLowerCase().includes(f.toLowerCase()));

const CURRENCY_FIELDS = ['revenue', 'charge', 'pay'];
const isCurrencyField = (field: string) =>
  CURRENCY_FIELDS.some((f) => field.toLowerCase().includes(f.toLowerCase())) ||
  field === 'margin' ||
  field === 'rate';

const isDescription = (field: string) =>
  field.toLowerCase().includes('description');

export const buildFormatting = (
  field: string,
  type: FleetOps.FieldType,
): MetricFormatting => {
  switch (type) {
    case 'date':
      return {
        prefix: undefined,
        postfix: 'duration',
        precision: undefined,
        positiveDeltaIsGood: undefined,
      };
    case 'float':
    case 'long':
    case 'int':
      return {
        prefix: isPercentageField(field)
          ? undefined
          : isCurrencyField(field)
            ? 'currency'
            : undefined,
        postfix: isPercentageField(field) ? 'percentage' : undefined,
        precision: type === 'int' || type === 'long' ? 0 : 2,
        positiveDeltaIsGood: true,
      };
    case 'geo_point': {
      return {
        prefix: undefined,
        postfix: undefined,
        precision: 8,
        positiveDeltaIsGood: undefined,
      };
    }
    default:
      return {
        prefix: undefined,
        postfix: undefined,
        precision: undefined,
        positiveDeltaIsGood: undefined,
      };
  }
};

const getIsBonusStatusField = ({
  field,
  scoringProgram,
}: {
  field: FleetOps.Field;
  scoringProgram?: BonusTypes.BonusProgram;
}): boolean => {
  if (!scoringProgram) {
    return false;
  }

  // TODO: Extend this to also look at criteria fields once Dave gets it in
  return scoringProgram.criteriaFields.some(
    (f) => f.statusField === field.field,
  );
};

const getIsBonusExpectationField = ({
  field,
  scoringProgram,
}: {
  field: FleetOps.Field;
  scoringProgram?: BonusTypes.BonusProgram;
}): boolean => {
  if (!scoringProgram) {
    return false;
  }

  return scoringProgram.criteriaFields.some(
    (f) => f.targetField === field.field,
  );
};

const getIsBonusRewardField = ({
  field,
  scoringProgram,
}: {
  field: FleetOps.Field;
  scoringProgram?: BonusTypes.BonusProgram;
}): boolean => {
  if (!scoringProgram) {
    return false;
  }

  return scoringProgram.rewardFields.some(
    (f) => f.rewardValueField === field.field,
  );
};

const getCellType = ({
  field,
  scoringProgram,
}: {
  field: FleetOps.Field;
  scoringProgram?: BonusTypes.BonusProgram;
}): FleetOps.CellType | undefined => {
  if (field.type === 'boolean') {
    if (field.field === QUALIFIES_FOR_BONUS_FIELD && !!scoringProgram) {
      return 'Bonus Eligibility Status';
    }
    return undefined;
  }

  if (field.type === 'date') {
    return 'Date';
  }

  if (field.type === 'float' || field.type === 'long' || field.type === 'int') {
    const isBonusRewardField = getIsBonusRewardField({
      field,
      scoringProgram,
    });
    if (isBonusRewardField) {
      return 'Bonus Reward Field';
    }

    const isBonusExpectationField = getIsBonusExpectationField({
      field,
      scoringProgram,
    });
    if (isBonusExpectationField) {
      return 'Bonus Expectation Field';
    }

    return isPercentageField(field.field)
      ? 'Percentage'
      : isCurrencyField(field.field)
        ? 'Currency'
        : 'Number';
  }

  if (field.type === 'geo_point') {
    return 'GeoPoint';
  }

  if (field.type === 'text') {
    const isBonusStatusField = getIsBonusStatusField({
      field,
      scoringProgram,
    });
    if (isBonusStatusField) {
      return 'Bonus Status Field';
    }

    return 'Text';
  }

  if (field.type === 'dateText') {
    return 'Text';
  }

  return undefined;
};

const buildBaseFieldViewDefault = (
  field: FleetOps.Field,
  dataset: FleetOps.DatasetDefinition,
  scoringProgram: BonusTypes.BonusProgram | undefined,
): FleetOps.BaseViewField => {
  const cellType = getCellType({
    field,
    scoringProgram,
  });
  switch (field.type) {
    case 'boolean':
      return {
        aggFunc: undefined,
        description: undefined,
        formatting: buildFormatting(field.field, field.type),
        nameAlias: field.alias,
        isVisible: true,
        dataset: dataset.type,
        field: field.field,
        cellType,
      };
    case 'date':
      return {
        aggFunc: undefined,
        description: undefined,
        formatting: buildFormatting(field.field, field.type),
        cellType,
        dateFormat: field.field.toLowerCase().includes('date')
          ? 'MMM/DD/YY'
          : 'MMM/DD/YY HH:mm',
        nameAlias: field.alias,
        isVisible: true,
        dataset: dataset.type,
        field: field.field,
      };
    case 'float':
    case 'long':
    case 'int':
      return {
        aggFunc: isDescription(field.field)
          ? undefined
          : field.aggFuncs.find((f) => f === 'sum'),
        description: undefined,
        formatting: buildFormatting(field.field, field.type),
        precision: field.type === 'int' || field.type === 'long' ? 0 : 2,
        cellType,
        nameAlias: field.alias,
        isVisible: true,
        dataset: dataset.type,
        field: field.field,
      };
    case 'geo_point':
      return {
        aggFunc: undefined,
        description: undefined,
        formatting: buildFormatting(field.field, field.type),
        precision: 8,
        cellType,
        nameAlias: field.alias,
        isVisible: true,
        dataset: dataset.type,
        field: field.field,
        geoPointDisplay: 'long, lat',
      };
    case 'text':
    default:
      return {
        aggFunc: isDescription(field.field)
          ? undefined
          : field.aggFuncs.find((f) => f === 'count'),
        description: undefined,
        formatting: buildFormatting(field.field, field.type),
        nameAlias: field.alias,
        isVisible: true,
        cellType,
        dataset: dataset.type,
        field: field.field,
      };
  }
};

export default buildBaseFieldViewDefault;
